/**
 * PhotoGrid · 详情页照片网格 · infinite scroll + 多选 + 右键菜单
 *
 * 数据流：父级（Detail/index.tsx）控制 photos / loading / hasMore / 多选态，
 * 本组件平铺渲染 + 触发 onLoadMore + 把每张照片包到 Dropdown 里支持右键。
 *
 * 多选交互（与 FolderList 一致）：
 *  · selectMode=false: hover 单图右上角出现勾选框（点它进入多选 + 把这张选上）
 *  · selectMode=true: 整图可点切换勾选 / 显示蓝色选中边框 / 浮层信息隐藏
 *  · 右键：始终触发上下文菜单（除非 disableContextMenu）
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { Dropdown, Spin } from 'antd'
import { CheckOutlined } from '@ant-design/icons'
import type { FolderListItem } from '../../../types/ai-classify'
import FolderIconSvg from '../components/FolderList/FolderIconSvg'
import styles from './Detail.module.scss'

export interface PhotoItem {
  id: string
  smallURL?: string
  largeURL?: string
  /** ms 时间戳 · 由 Detail/index.tsx 已统一转换 */
  time: number
  name?: string
  /** 角标类型 · 后端字段；本版没角标也无碍 */
  badge?: string
  /**
   * 后端原 file 字段 · 大文件原图 URL，与 smallURL 不同
   * 用作 exportPhotosAsExcel / batchRemoveWatermarkCheck 的去重 key（按 URL）
   */
  file?: string
  /**
   * 后端 seq 字段 · 同一相册内的稳定序号
   * 用作 photoPicker / batch-editor 等去重 key（与 file 互补）
   */
  seq?: string | number
  /** 拍摄人昵称 · 旧"分类文件夹"详情页的 photo card 第 1 行展示字段 */
  userNickname?: string
  /** 地点（addr）· 旧"分类文件夹"详情页 photo card 第 3 行展示字段 */
  addr?: string
  /** 媒体类型 · 0-图片 1-视频；ViewerV2 据此选 largeURL / videoURL 请求水印 */
  mediaType?: number
  /** 视频地址 · mediaType=1 时大图预览 / 水印请求用 */
  videoURL?: string
  /** 拍摄人 userID · 预览"添加到文件夹"按钮判断是否本人照片用 */
  userID?: string
}

interface PhotoGridProps {
  photos: PhotoItem[]
  loading: boolean
  hasMore: boolean
  onLoadMore: () => void
  /** 多选模式 · 整图点击切换勾选；展示蓝边框 */
  selectMode?: boolean
  /** 选中 photoId 集合 · 用 Set 比 includes O(n) 快 */
  selectedIds?: Set<string>
  /** 切换单条勾选 · 多选模式下整图点击触发；非多选下 hover 勾选框点击触发 */
  onToggleSelect?: (id: string) => void
  /**
   * 拖框选完成 · 把命中的 photoId 列表整体替换给父级，让父级进入多选 + 设选中
   * 与列表页 FolderList 框选语义一致；空命中不触发回调（不会清空已有选择）
   */
  onMarqueeSelect?: (ids: string[]) => void
  /**
   * 单图点击预览 · 仅非多选模式触发；父级用 ViewerV2 打开大图
   * 与 photoFolders/Detail 同款交互：多选模式下点击切换勾选，非多选下点击预览
   */
  onPreview?: (photoId: string) => void
  /**
   * 右键菜单内容 · 父级组件按需返回 <Menu>；返 null 则不渲染菜单
   * 给当前 photo 用，包到 antd Dropdown trigger=['contextMenu'] 里
   */
  buildContextMenu?: (photo: PhotoItem) => React.ReactElement | null
  /**
   * 子文件夹 · 与照片网格融合渲染（置顶 section）
   *  · 与照片格子共用同一组列宽（minmax 160px / 1fr）让两者宽度一致
   *  · 卡片是横向布局（左 icon + 右名称），高度比照片格子矮
   *  · 不展示张数 / 子级数（保持紧凑）
   *  · 多选模式下不参与勾选；点击直接跳子文件夹详情
   */
  subFolders?: FolderListItem[]
  onSelectSubFolder?: (folderId: string) => void
  /**
   * 子文件夹多选（仅详情页 grid）· 传入 onSubFolderSelectClick 即渲染勾选框
   *  · selectMode：整卡点击 toggle（替代跳转）；勾选框 hover 可见、多选态常显
   */
  subFolderSelectMode?: boolean
  subFolderSelectedIds?: Set<string>
  onSubFolderSelectClick?: (folderId: string) => void
  /** 子文件夹 section 头部覆盖（多选态父级传工具条）· 不传走默认「子文件夹 · N 个」 */
  subFolderHeader?: React.ReactNode
}

const PhotoGrid: React.FC<PhotoGridProps> = ({
  photos,
  loading,
  hasMore,
  onLoadMore,
  selectMode = false,
  selectedIds,
  onToggleSelect,
  onMarqueeSelect,
  onPreview,
  buildContextMenu,
  subFolders,
  onSelectSubFolder,
  subFolderSelectMode = false,
  subFolderSelectedIds,
  onSubFolderSelectClick,
  subFolderHeader
}) => {
  const sentinelRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  /**
   * 鼠标拖框选 state · 与列表页 FolderList 同款实现
   *  · null = 没在拖；非空 = mousedown 已触发，等 move/up
   *  · active=true 表示已超过阈值（开始绘制框 + 真正进入多选）
   *  · 坐标用 clientX/Y（viewport 相对），方便和 getBoundingClientRect 对齐
   */
  const [marquee, setMarquee] = useState<{
    startX: number
    startY: number
    endX: number
    endY: number
    active: boolean
  } | null>(null)

  /**
   * 容器 mousedown · 仅当起点在"空白处"才进入框选轨道
   *  · 起点在 [data-photo-id] 元素内 → 让卡片自己的 onClick / Dropdown 接管
   *  · 起点在 [data-no-marquee]（子文件夹 section / 日期标题等）→ 跳过
   *  · 否则记录起点，等 move 超阈值才正式进入 active 状态
   */
  const handleContainerMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      if (e.button !== 0) return
      if (!onMarqueeSelect) return
      const target = e.target as HTMLElement
      if (target.closest('[data-photo-id]')) return
      if (target.closest('[data-no-marquee]')) return
      // 阻止默认 · mousedown 否则会启动浏览器的文本选择，与拖框冲突
      e.preventDefault()
      setMarquee({
        startX: e.clientX,
        startY: e.clientY,
        endX: e.clientX,
        endY: e.clientY,
        active: false
      })
    },
    [onMarqueeSelect]
  )

  /**
   * 框选完成 · 计算落入框内的 photo-id 列表 → 通过 onMarqueeSelect 上抛
   * 命中为空时不触发回调（避免误清空当前 selection）
   */
  const finalizeMarquee = useCallback(
    (m: {
      startX: number
      startY: number
      endX: number
      endY: number
    }): void => {
      const left = Math.min(m.startX, m.endX)
      const right = Math.max(m.startX, m.endX)
      const top = Math.min(m.startY, m.endY)
      const bottom = Math.max(m.startY, m.endY)
      const root = containerRef.current
      if (!root || !onMarqueeSelect) return
      const all = root.querySelectorAll('[data-photo-id]')
      const hits: string[] = []
      all.forEach((el) => {
        const r = el.getBoundingClientRect()
        if (
          r.right >= left &&
          r.left <= right &&
          r.bottom >= top &&
          r.top <= bottom
        ) {
          const id = el.getAttribute('data-photo-id')
          if (id) hits.push(id)
        }
      })
      if (hits.length === 0) return
      onMarqueeSelect(hits)
    },
    [onMarqueeSelect]
  )

  /**
   * 全局 mousemove / mouseup / Esc 监听 · 仅 marquee 非空时生效
   * 与 FolderList 的同款做法 · deps 用 !!marquee 避免 every move 重绑
   */
  useEffect(() => {
    if (!marquee) return
    const prevUserSelect = document.body.style.userSelect
    document.body.style.userSelect = 'none'
    if (window.getSelection) {
      const sel = window.getSelection()
      if (sel) sel.removeAllRanges()
    }
    const onMove = (e: MouseEvent): void => {
      e.preventDefault()
      setMarquee((prev) => {
        if (!prev) return null
        const moved =
          Math.hypot(e.clientX - prev.startX, e.clientY - prev.startY) > 5
        return {
          ...prev,
          endX: e.clientX,
          endY: e.clientY,
          active: prev.active || moved
        }
      })
    }
    const onUp = (): void => {
      setMarquee((prev) => {
        if (prev?.active) finalizeMarquee(prev)
        return null
      })
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setMarquee(null)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      window.removeEventListener('keydown', onKey)
      document.body.style.userSelect = prevUserSelect
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!marquee])

  /**
   * IntersectionObserver 监听 sentinel · 进入视口即触发 onLoadMore
   *  · loading / hasMore 用 ref 存最新值，避免每次重建 observer
   */
  const onLoadMoreRef = useRef(onLoadMore)
  const stateRef = useRef({ loading, hasMore })
  useEffect(() => {
    onLoadMoreRef.current = onLoadMore
    stateRef.current = { loading, hasMore }
  }, [onLoadMore, loading, hasMore])

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (
            entry.isIntersecting &&
            stateRef.current.hasMore &&
            !stateRef.current.loading
          ) {
            onLoadMoreRef.current()
          }
        }
      },
      { rootMargin: '200px' }
    )
    io.observe(sentinel)
    return () => io.disconnect()
  }, [])

  const hasSubFolders = !!subFolders && subFolders.length > 0

  return (
    <div
      className={styles.photoSection}
      ref={containerRef}
      onMouseDown={handleContainerMouseDown}
    >
      {/* 子文件夹置顶 section · 标题区 + 子文件夹卡都不参与照片框选 */}
      {hasSubFolders && (
        <div className={styles.subFolderSection} data-no-marquee>
          <div className={styles.subFolderSectionHeader}>
            {subFolderHeader || (
              <>
                <span className={styles.subFolderSectionTitle}>子文件夹</span>
                <span className={styles.subFolderSectionCount}>
                  {subFolders!.length} 个
                </span>
              </>
            )}
          </div>
          <div className={styles.subFolderInlineGrid}>
            {subFolders!.map((sf) => {
              // 智能分类文件夹统一黄色底，靠 isAi 触发右下角刷新水印区分
              const isAi = sf.aiGenerated === true
              const hasChildren = sf.subfolderCount > 0
              const sfSelected =
                !!subFolderSelectedIds && subFolderSelectedIds.has(sf.folderId)
              return (
                <div
                  key={sf.folderId}
                  className={classNames(styles.subFolderInlineCard, {
                    [styles.subFolderInlineCardSelectable]: subFolderSelectMode,
                    [styles.subFolderInlineCardSelected]:
                      subFolderSelectMode && sfSelected
                  })}
                  onClick={() => {
                    if (subFolderSelectMode) {
                      onSubFolderSelectClick &&
                        onSubFolderSelectClick(sf.folderId)
                    } else {
                      onSelectSubFolder && onSelectSubFolder(sf.folderId)
                    }
                  }}
                  role="button"
                  aria-label={`打开子文件夹 ${sf.folderName}`}
                >
                  {onSubFolderSelectClick && (
                    <span
                      className={classNames(styles.subFolderSelectIndicator, {
                        [styles.subFolderSelectIndicatorChecked]: sfSelected,
                        [styles.subFolderSelectIndicatorAlways]:
                          subFolderSelectMode
                      })}
                      onClick={(e) => {
                        e.stopPropagation()
                        onSubFolderSelectClick(sf.folderId)
                      }}
                      role="checkbox"
                      aria-checked={sfSelected}
                      aria-label={sfSelected ? '取消勾选' : '勾选'}
                    >
                      {sfSelected && (
                        <CheckOutlined
                          style={{ fontSize: 12, color: '#fff' }}
                        />
                      )}
                    </span>
                  )}
                  <div className={styles.subFolderInlineIcon}>
                    <FolderIconSvg
                      size={120}
                      variant="manual"
                      isAi={isAi}
                      hasChildren={hasChildren}
                    />
                  </div>
                  <div
                    className={styles.subFolderInlineName}
                    title={sf.folderName}
                  >
                    {sf.folderName}
                  </div>
                  <div className={styles.subFolderInlineCount}>
                    {sf.photoCount.toLocaleString('en-US')} 张
                    {hasChildren && (
                      <span className={styles.subFolderInlineCountSep}>
                        · {sf.subfolderCount} 子文件夹
                      </span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
      {/* 照片平铺网格 */}
      <div className={styles.photoGroupGrid}>
        {photos.map((p) => {
          const selected = !!selectedIds && selectedIds.has(p.id)
          const cell = (
            <div
              className={classNames(styles.photoCell, {
                [styles.photoCellSelectable]: selectMode,
                [styles.photoCellSelected]: selected
              })}
              onClick={() => {
                if (selectMode && onToggleSelect) {
                  onToggleSelect(p.id)
                } else if (!selectMode && onPreview) {
                  onPreview(p.id)
                }
              }}
              data-photo-id={p.id}
            >
              <div className={styles.photoMedia}>
                {p.smallURL ? (
                  <img
                    className={styles.photoImg}
                    src={p.smallURL}
                    alt={p.name || ''}
                    loading="lazy"
                  />
                ) : (
                  <div className={styles.photoPlaceholder} />
                )}
                {onToggleSelect && (
                  <span
                    className={classNames(styles.photoCheckbox, {
                      [styles.photoCheckboxChecked]: selected,
                      [styles.photoCheckboxAlways]: selectMode
                    })}
                    onClick={(e) => {
                      e.stopPropagation()
                      onToggleSelect(p.id)
                    }}
                    role="checkbox"
                    aria-checked={selected}
                    aria-label={selected ? '取消勾选这张照片' : '勾选这张照片'}
                  >
                    {selected && <CheckOutlined />}
                  </span>
                )}
              </div>
              <div className={styles.photoMeta}>
                <div
                  className={classNames(
                    styles.photoMetaLine,
                    styles.photoMetaUser
                  )}
                  title={p.userNickname || ''}
                  onClick={(e) => e.stopPropagation()}
                >
                  {p.userNickname || '—'}
                </div>
                <div className={styles.photoMetaLine}>
                  {formatPhotoDateTime(p.time)}
                </div>
                <div
                  className={styles.photoMetaLine}
                  title={p.addr || ''}
                  onClick={(e) => e.stopPropagation()}
                >
                  {p.addr || '—'}
                </div>
              </div>
            </div>
          )
          if (!buildContextMenu) {
            return <React.Fragment key={p.id}>{cell}</React.Fragment>
          }
          const menu = buildContextMenu(p)
          if (!menu) {
            return <React.Fragment key={p.id}>{cell}</React.Fragment>
          }
          return (
            <Dropdown
              key={p.id}
              popupRender={() => menu}
              trigger={['contextMenu']}
            >
              {cell}
            </Dropdown>
          )
        })}
      </div>

      {/* infinite scroll sentinel · 进入视口触发 onLoadMore */}
      <div ref={sentinelRef} className={styles.sentinel} />

      {loading && (
        <div className={styles.loadingMore}>
          <Spin size="small" />
          <span style={{ marginLeft: 8 }}>加载中…</span>
        </div>
      )}
      {!hasMore && photos.length > 0 && (
        <div className={styles.endHint}>已加载全部</div>
      )}

      {/* 框选可视化矩形 · position:fixed 用 client 坐标直接定位
          仅 marquee.active=true（已超阈值）才渲染，避免点击瞬间闪一个零尺寸框 */}
      {marquee && marquee.active && (
        <div
          className={styles.marqueeRect}
          style={{
            left: Math.min(marquee.startX, marquee.endX),
            top: Math.min(marquee.startY, marquee.endY),
            width: Math.abs(marquee.endX - marquee.startX),
            height: Math.abs(marquee.endY - marquee.startY)
          }}
        />
      )}
    </div>
  )
}

/**
 * ms 时间戳 → 'YYYY-MM-DD HH:mm'（跨年完整）/ 'MM-DD HH:mm'（同年简短）
 *
 * 与旧"分类文件夹"详情页 photo card 同款时间格式：
 *  · 同年   05-07 14:32
 *  · 跨年   2025-12-31 23:59
 */
function formatPhotoDateTime(ts: number): string {
  if (!ts) return '—'
  const d = new Date(ts)
  const now = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return y === now.getFullYear()
    ? `${m}-${day} ${hh}:${mm}`
    : `${y}-${m}-${day} ${hh}:${mm}`
}

export default PhotoGrid
